import ExcelJS from 'exceljs';
import { notesApi } from '@/lib/notesApi';
import { calibriWorkbook } from './tagFont';
import { imagePng } from './tagExcel';
import { rateLabel } from './tagPricing';
import { buildTagDataFromDetail, tagMaterials } from '@/components/jewellery/JewelleryTagViewer';

const NAVY = 'FF1E2A4A';
const AMBER = 'FFB45309';
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const d8 = (s) => {
  if (!s) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' }).formatToParts(new Date(s)).map((x) => [x.type, x.value]));
  return `${p.day} ${MON[Number(p.month) - 1]} ${p.year}`;
};

export const loadRepairHistory = async (pieceId) => (await notesApi.get(`/notes/tag-workspace/pieces/${pieceId}/repairs`)).data;

// The piece as it was before the first repair, then after each repair (with its received date).
export function repairVersions({ current, repairs }) {
  if (!repairs.length) return [{ key: 'cur', title: 'Current', sub: '', det: current, post: false }];
  const out = [];
  let shownAfter = false;
  repairs.forEach((r, i) => {
    if (i === 0 || !shownAfter) out.push({ key: `b${r.n}`, title: `Before repair ${r.n}`, sub: `Sent for repairing ${d8(r.sent_at) || '—'}${r.received_at ? '' : ' · still in repair'}`, det: r.before, post: false });
    else out[out.length - 1].sub += ` · sent again ${d8(r.sent_at)}${r.received_at ? '' : ' (still in repair)'}`;
    shownAfter = false;
    if (r.received_at) {
      const det = r.after || repairs[i + 1]?.before || (i === repairs.length - 1 ? current : null);
      out.push({ key: `a${r.n}`, title: `After repair ${r.n}`, sub: `Received after repairing ${d8(r.received_at)}`, det, post: true });
      shownAfter = !!det;
    }
  });
  return out;
}

const thin = { style: 'thin', color: { argb: 'FFB8C0CC' } };
const thick = { style: 'medium', color: { argb: NAVY } };

// Repair history Excel: every version as its own product block (our vertical format), side by side,
// one blank column between blocks, each block boxed; post-repair blocks carry the received date.
export async function downloadRepairHistory(pieceId, prefetched) {
  const hist = prefetched || await loadRepairHistory(pieceId);
  const vers = repairVersions(hist);
  const wb = new ExcelJS.Workbook(); calibriWorkbook(wb);
  const ws = wb.addWorksheet('Repair history');
  const W = 5; // 4 columns per block + 1 blank
  vers.forEach((_, i) => { const c = 1 + i * W; [26, 11, 6, 7, 3].forEach((w, k) => { ws.getColumn(c + k).width = w; }); });
  const blocks = await Promise.all(vers.map(async (v) => {
    if (!v.det) return { v, rows: null, img: null };
    const data = buildTagDataFromDetail({ ...v.det, _allOpts: true });
    const rows = [
      ['Jewellery no.', data.jewelleryNo], ['Type', data.jewelleryType || '—'], ['Gold purity', data.purityText || '—'],
      ['Gross weight', Number(data.gross.toFixed(3)), 'g'], ['Net weight', Number(data.net.toFixed(3)), 'g'],
      ...(data.separate || []).map((s) => [s.label, Number(Number(s.qty).toFixed(3)), s.unit]),
      ...tagMaterials(data, 'priced').map((m) => [rateLabel(m), Number(Number(m.carats || 0).toFixed(2)), 'ct', Number(m.pieces || 0) || null]),
    ];
    return { v, rows, img: await imagePng(data.image) };
  }));
  const height = 3 + Math.max(1, ...blocks.map((b) => (b.rows ? b.rows.length + 1 : 1)));
  for (let r = 1; r <= height; r += 1) ws.getRow(r);
  ws.getRow(3).height = 92;
  blocks.forEach(({ v, rows, img }, i) => {
    const c = 1 + i * W;
    const cell = (r, k) => ws.getRow(r).getCell(c + k);
    ws.mergeCells(1, c, 1, c + 3); ws.mergeCells(2, c, 2, c + 3); ws.mergeCells(3, c, 3, c + 3);
    Object.assign(cell(1, 0), { value: v.title, font: { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: v.post ? 'FF047857' : NAVY } }, alignment: { horizontal: 'center' } });
    Object.assign(cell(2, 0), { value: v.sub, font: { bold: v.post, color: { argb: v.post ? 'FF047857' : AMBER } }, alignment: { horizontal: 'center' } });
    if (!rows) {
      cell(3, 0).value = 'Details before this repair were not recorded';
      cell(3, 0).alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
    } else {
      if (img) { const k = Math.min(110 / img.w, 110 / img.h); ws.addImage(wb.addImage({ base64: img.base64, extension: 'png' }), { tl: { col: c - 1 + 0.6, row: 2 + 0.08 }, ext: { width: img.w * k, height: img.h * k }, editAs: 'oneCell' }); }
      ['Item', 'Qty', 'Unit', 'Pcs'].forEach((x, k) => Object.assign(cell(4, k), { value: x, font: { bold: true, color: { argb: 'FFFFFFFF' } }, fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } } }));
      rows.forEach((row, n) => {
        const r = 5 + n;
        row.forEach((x, k) => { cell(r, k).value = x ?? null; });
        if (typeof row[1] === 'number') cell(r, 1).numFmt = row[2] === 'g' ? '0.000' : '0.00';
        if (n < 5) cell(r, 0).font = { bold: true };
      });
    }
    // Box each block: thin grid inside, medium outline.
    for (let r = 1; r <= height; r += 1) {
      for (let k = 0; k < 4; k += 1) {
        cell(r, k).border = { top: r === 1 ? thick : thin, bottom: r === height ? thick : thin, left: k === 0 ? thick : thin, right: k === 3 ? thick : thin };
      }
    }
    // Merged title / date / image rows: the master cell carries both outline sides.
    for (let r = 1; r <= 3; r += 1) cell(r, 0).border = { top: r === 1 ? thick : thin, bottom: thin, left: thick, right: thick };
  });
  const buf = await wb.xlsx.writeBuffer();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  a.download = `Repair history - ${hist.current.jewellery_no}.xlsx`; a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return hist.repairs.length;
}
